/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import * as Sentry from '@sentry/browser'

import {
  RequestAllocate,
  RequestCapture,
  RequestValidate,
  RequestWorkflow,
  TextArea,
  SvgIcon
} from '../'

import { normalizePhoneNumber } from '../../utils'

const isUserContentCaptor = (requestRun) => (
  requestRun.capturedBy
    ? requestRun.capturedBy.id === localStorage.getItem('userNodeId')
    : false
)

const saveNewPrimaryMsisdn = (addressId, addressValue, identity, addressMutate) => {
  let input = {
    default: true
  }

  if (addressId) {
    input.id = addressId  // necessary when identity's existing numbers becomes new primary
  } else {
    input.addressValue = normalizePhoneNumber(addressValue)
    input.addressType = 'msisdn'
    input.identity = identity
    input.default = true
  }

  addressMutate({ variables: { input } })
    .catch(error => Sentry.captureMessage(`Failed to save default phone number - ${error}`))
}

const MUTATION_ADDRESS = gql`
  mutation($input: AddressMutationInput!) {
    addressMutate(input: $input) {
      address {
        id
        default
      }
    }
  }
`

const renderStepContent = (requestRun, step, requestrunMutate, refetchData, onCreateCaseClick, saveRequestAssignedTo, workflowrunProcessingAtStep, onWorkflowRefreshClick) => {
  const updateRequestRun = (input) => requestrunMutate({ variables: { input } })

  switch(step) {
    case 'allocate':
      return (
        <Mutation mutation={MUTATION_ADDRESS}>
          {addressMutate => (
            <RequestAllocate
              requestRun={requestRun}
              open={true}
              updateRequestRun={updateRequestRun}
              saveNewPrimaryMsisdn={(addressId, addressValue, identity) => (
                saveNewPrimaryMsisdn(addressId, addressValue, identity, addressMutate)
              )}
              onCreateCaseClick={onCreateCaseClick}
              refreshData={refetchData}
              singleItemCondensedView={true}
            />
          )}
        </Mutation>
      )

    case 'capture':
      return (
        <React.Fragment>
          {requestRun.notes && requestRun.notes.length > 0 &&
            <TextArea
              label={
                <span className='u-font-size-large'>
                  <SvgIcon
                    variant='utility'
                    symbol='info'
                    colour='light'
                    size='small'
                    className='slds-p-right_x-small'
                  />
                  Reviewer notes
                </span>
              }
              value={requestRun.notes}
              disabled={true}
              rows={5}
              className='slds-m-around_small'
            />
          }
          <RequestCapture
            requestRun={requestRun}
            requestType={requestRun.requesttype.id}
            open={true}
            updateRequestRun={updateRequestRun}
            singleItemCondensedView={true}
            saveProgress={() => saveRequestAssignedTo(requestrunMutate, requestRun.id, 'null')}
          />
        </React.Fragment>
      )

    case 'validate':
      return (
        <RequestValidate
          requestRun={requestRun}
          open={true}
          updateRequestRun={updateRequestRun}
          // userIsContentCaptor={isUserContentCaptor(requestRun)}
          singleItemCondensedView={true}
        />
      )

    case 'workflow':
      const workflowInitiated = requestRun.workflowrunProcessing
      if (workflowInitiated) {
        return (
          <RequestWorkflow
            requestRun={requestRun}
            open={true}
            singleItemCondensedView={true}
            onRefreshClick={onWorkflowRefreshClick}
          />
        )
      } else {
        return (
          <div className='slds-m-left_small slds-m-top_small'>
            <h3 className='slds-section__title u-font-size-x-large'>
              Workflow
            </h3>
            <p className='slds-m-top_large u-font-size-large'>
              The workflow for this request is now ready to be initiated.
            </p>
          </div>
        )
      }

    default:
      return null
  }
}


export const RequestProcessingStep = ({ requestRun, step, requestrunMutate, refetchData, onCreateCaseClick, saveRequestAssignedTo, workflowrunProcessingAtStep, onWorkflowRefreshClick }) => (
  <React.Fragment>
    {renderStepContent(requestRun, step, requestrunMutate, refetchData, onCreateCaseClick, saveRequestAssignedTo, workflowrunProcessingAtStep, onWorkflowRefreshClick)}
  </React.Fragment>
)

RequestProcessingStep.propTypes = {
  requestRun: PropTypes.object.isRequired,
  step: PropTypes.string.isRequired,
  requestrunMutate: PropTypes.func,
  onCreateCaseClick: PropTypes.func,
  saveRequestAssignedTo: PropTypes.func,
  refetchData: PropTypes.func,
  onWorkflowRefreshClick: PropTypes.func
}



// WEBPACK FOOTER //
// ./src/components/request-processing-step/index.js